import React, { useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Typography,
    Snackbar,
    Alert,
    useMediaQuery,
    useTheme,
    Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

const API_URL = window.location.href.includes("localhost")
    ? process.env.REACT_APP_API_URL_LOCAL
    : process.env.REACT_APP_API_URL_PROD;


const ClientsTable = () => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const fetchClients = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${API_URL}api/get-customers-for-admin?page=${page + 1}&limit=${pageSize}`
            );
            if (res.data.success) {
                setRows(
                    res.data.data.map((item) => {
                        // normalize id to string (handles {_id: { $oid: "..." }} too)
                        let idVal = item._id;
                        if (idVal && typeof idVal === "object") {
                            idVal = idVal.$oid || idVal.toString();
                        }

                        return {
                            id: idVal,
                            name: item.name || "",
                            email: item.email || "",
                            phone: item.phone || "",
                            age: item.age || "",
                            address: item.address || "",
                            source: (item.source.toUpperCase().replace('_', ' ')) || "",
                            // use the helper for a reliable formatted string
                            createdAt: item.createdAt,
                            // keep raw if you need it later
                            _createdAtRaw: item.createdAt,
                        };
                    })
                );
                setRowCount(res.data.total);
            }
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: "Failed to fetch clients",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRowEditCommit = async (id, updatedRow) => {
        try {
            const res = await axios.put(
                `${API_URL}api/edit/customers-for-admin/${id}`,
                updatedRow
            );
            if (res.data.success) {
                setSnackbar({
                    open: true,
                    message: "Client updated successfully!",
                    severity: "success",
                });
            } else {
                setSnackbar({
                    open: true,
                    message: "Update failed",
                    severity: "error",
                });
            }
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: "Error updating client",
                severity: "error",
            });
        }
    };

    useEffect(() => {
        fetchClients();
    }, [page, pageSize]);

    const columns = [
        { field: "name", headerName: "Name", flex: 1, editable: true, minWidth: 120 },
        { field: "email", headerName: "Email", flex: 1.2, editable: true, minWidth: 160 },
        { field: "phone", headerName: "Phone", flex: 1, editable: true, minWidth: 140 },
        { field: "age", headerName: "Age", width: 90, editable: true },
        { field: "address", headerName: "Address", flex: 1.2, editable: true, minWidth: 150 },
        { field: "source", headerName: "Source", width: 120, editable: true },
        { field: "createdAt", headerName: "Created At", width: 200 },
    ];

    return (
        <ContainerWrapper>
            <Paper
                elevation={3}
                sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    height: "100%",
                }}
            >
                <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    gutterBottom
                    textAlign="center"
                >
                    Clients List
                </Typography>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            height: { xs: "65vh", sm: "75vh", md: "80vh" },
                            width: "100%",
                            mt: 2,
                        }}
                    >
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            pagination
                            paginationMode="server"
                            pageSize={pageSize}
                            rowsPerPageOptions={[10, 20, 50]}
                            rowCount={rowCount}
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={(newSize) => {
                                setPageSize(newSize);
                                setPage(0);
                            }}
                            disableSelectionOnClick
                            experimentalFeatures={{ newEditingApi: true }}
                            processRowUpdate={(newRow) => {
                                handleRowEditCommit(newRow.id, newRow);
                                return newRow;
                            }}
                            sx={{
                                "& .MuiDataGrid-cell": { outline: "none", fontSize: { xs: "0.8rem", sm: "0.9rem" } },
                                "& .MuiDataGrid-columnHeaders": {
                                    backgroundColor: "#f5f5f5",
                                    fontWeight: "bold",
                                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                },
                            }}
                        />
                    </Box>
                )}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert severity={snackbar.severity} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Paper>
        </ContainerWrapper>
    );
};

const ContainerWrapper = ({ children }) => (
    <Box
        sx={{
            width: "100%",
            p: { xs: 1, sm: 3 },
            mt: { xs: 2, sm: 3 },
            display: "flex",
            justifyContent: "center",
        }}
    >
        <Box sx={{ width: "100%", maxWidth: "1200px" }}>{children}</Box>
    </Box>
);

export default ClientsTable;
